<script setup>
import {onBeforeMount, reactive, ref} from 'vue'
import {Feedback, State, clone, getModel} from '@/paks/vu-app'
import {removeDisplay, updateDisplay} from './DashUtils.js'

const props = defineProps({
    display: Object,
})

const emit = defineEmits(['input'])

const page = reactive({
    basis: 'Standard',
    deleting: false,
    display: {},
    displays: [],
    existing: false,
    expressionType: 'Responsive',
    prior: null,
    saving: false,
    showSaveAs: false,
    saveAsName: '',
})

//  Component refs
const self = ref(null)
const confirm = ref(null)

onBeforeMount(() => {
    if (props.display) {
        page.display = clone(props.display)
    }
    let display = page.display
    if (display.name) {
        page.existing = true
    } else {
        display = clone(State.app.standardDisplay.displays[0])
        display.name = null
        display.expression = null
    }
    page.display = display
    page.prior = clone(display)
    page.displays = State.app.fullDisplay.displays
    setType()
})

async function save() {
    let display = page.display
    if (!display.name) {
        throw new Error('Must set display name')
    }
    await updateDisplay(page.display)
    if (page.prior && page.prior.name != display.name) {
        await removeDisplay(page.prior)
    }
    State.app.setNeed('repaint')
    await confirm.value.ask('Please wait a minute for the app to be deployed', {disagree: false})
    emit('input')
}

async function saveAs() {
    page.showSaveAs = true
    page.prior = null
}

async function saveAsComplete() {
    page.showSaveAs = false
    page.display.name = page.display.saveAsName
    await save()
}

async function deleteDisplay() {
    let display = page.display
    if (!display.name) {
        return
    }
    if (await confirm.value.ask(`Do you want to delete the display ${display.name}"?`)) {
        page.deleting = true
        await removeDisplay(display)
        Feedback.info('Display Deleted')
        page.deleting = false
    }
    emit('input')
}

async function changeBasis() {
    let display = clone(State.app.standardDisplay.displays[0])
    if (page.basis == 'Minimal') {
        page.display.views = selectViews(null, display.views, ['/login', '/logout', '/designer'])
    } else {
        page.display.views = clone(display.views)
    }
    display.name = page.display.name
    display.expression = page.display.expression
    page.display = display
}

//  Select views based on the allow list which contains top level paths
function selectViews(parentPath, views, allow, result = []) {
    for (let view of views) {
        let path
        if (parentPath == '/') {
            path = '/' + view.path
        } else if (parentPath) {
            path = parentPath + '/' + view.path
        } else {
            path = view.path
        }
        if (path == '/') {
            result.push(view)
        } else if (allow.find((p) => path.indexOf(p) >= 0)) {
            result.push(view)
        } 
        if (view.views) {
            view.views = selectViews(path, view.views, allow, [])
        }
    }
    return result.length > 0 ? result : null
}

function changeType() {
    let type = page.expressionType
    let display = page.display
    if (type == 'Responsive') {
        display.expression = ''
        display.features.nav.sidebarHide = false
    } else if (type == 'Mobile') {
        display.expression = 'width < 640'
        display.features.nav.sidebarHide = true
    } else if (type == 'Tablet') {
        display.expression = '640 <= width && width < 1024'
        display.features.nav.sidebarHide = false
    } else if (type == 'Desktop') {
        display.expression = 'width >= 1024'
        display.features.nav.sidebarHide = false
    } else if (type == 'Custom') {
        display.expression = ''
        display.features.nav.sidebarHide = false
    }
}

function setType() {
    let display = page.display
    let exp = display.expression
    if (exp == '') {
        page.expressionType = 'Responsive'
    } else if (exp == 'width < 640') {
        page.expressionType = 'Mobile'
    } else if (exp == '640 <= width && width < 1024') {
        page.expressionType = 'Tablet'
    } else if (exp == 'width >= 1024') {
        page.expressionType = 'Desktop'
    } else {
        page.expressionType = 'Custom'
    } 
}

function cancel() {
    page.showSaveAs = false
}
</script>

<template>
    <vu-form
        class="display-edit"
        help="/doc/ui/display/display-edit.html"
        ref="self"
        :data="page"
        :save="save"
        :title="`${page.display.name ? 'Modify' : 'Add'} Display`">
        <vu-input
            v-model="page.display.name"
            label="Display Name"
            name="name"
            placeholder="Enter display name"
            type="text"
            class="mb-2" />

        <vu-input
            v-model="page.display.title"
            label="Display App Title"
            name="title"
            placeholder="Enter displayed App title"
            type="text"
            class="mb-2" />

        <vu-input
            v-if="!page.existing"
            v-model="page.basis"
            label="Page Set"
            type="radio"
            :items="['Minimal', 'Standard']"
            @change="changeBasis" />

        <vu-input
            v-model="page.expressionType"
            label="Expression Type"
            type="radio"
            :items="['Responsive', 'Mobile', 'Tablet', 'Desktop', 'Custom']"
            @change="changeType" />

        <vu-input
            v-if="page.expressionType != 'Responsive'"
            :disabled="page.expressionType == 'Custom' ? false : true"
            v-model="page.display.expression"
            label="Display Matching Expression"
            placeholder="Expression with 'width', 'agent', 'height', 'language'"
            name="expression"
            type="text"
            class="mb-2" />

        <div class="actions">
            <v-btn size="small" color="accent" type="submit" :loading="page.saving">Save</v-btn>
            <v-btn size="small" color="teal" @click="saveAs">Save As</v-btn>
            <v-btn size="small" color="none" @click="emit('input', null)">Cancel</v-btn>
            <v-btn :disabled="page.displays.length <= 1" size="small" color="error" :loading="page.deleting" @click="deleteDisplay">Delete</v-btn>
        </div>

        <vu-confirm ref="confirm" />
    </vu-form>
    <v-dialog v-model="page.showSaveAs" width="550" content-class="save-as-dialog">
        <v-card>
            <v-card-title class="headline" primary-title>Save Display As</v-card-title>
            <v-card-text class="mt-4 px-4"><label>Enter a new name for the display</label></v-card-text>
            <v-card-text class="px-4">
                <v-text-field v-model="page.display.saveAsName" single-line label="Display Name" />
            </v-card-text>
            <v-card-actions class="save-as-actions px-4">
                <v-btn
                    ripple
                    color="accent"
                    variant="elevated"
                    :loading="page.saving"
                    type="submit"
                    @click="saveAsComplete">
                    Save
                </v-btn>
                <v-btn ripple variant="elevated" @click="cancel">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss">
.save-as-dialog {
    .headline {
        background: rgb(var(--v-theme-primary));
        color: white;
        font-weight: bold;
        padding: 16px;
    }
}
.save-as-actions {
    margin: 16px 0 16px 0;
    flex: 0 0 100%;
    justify-content: flex-start !important;
    .v-btn {
        margin-right: 10px;
        @media (max-width: 640px) {
            margin-right: 6px;
            padding: 0 6px;
        }
    }
}
</style>
